import { useEffect, useRef, useState, useCallback } from 'react';

export function useParallax(speed = 0.3) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const rect = el.getBoundingClientRect();
          const windowH = window.innerHeight;
          const visible = rect.top < windowH && rect.bottom > 0;
          if (visible) {
            const progress = (windowH - rect.top) / (windowH + rect.height);
            const offset = (progress - 0.5) * speed * rect.height;
            el.style.transform = `translate3d(0, ${offset}px, 0)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [speed]);

  return ref;
}

export function useReveal(threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  const observerRef = useRef(null);

  const ref = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    observerRef.current.observe(node);
  }, [threshold]);

  return [ref, visible];
}

export function useHeroParallax() {
  const bgRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const windowH = window.innerHeight;
          if (scrollY < windowH * 1.5) {
            const progress = scrollY / windowH;
            if (bgRef.current) {
              bgRef.current.style.transform = `translate3d(0, ${scrollY * 0.4}px, 0) scale(${1 + progress * 0.08})`;
            }
            if (contentRef.current) {
              contentRef.current.style.opacity = `${1 - progress * 1.2}`;
              contentRef.current.style.transform = `translate3d(0, ${scrollY * -0.15}px, 0)`;
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { bgRef, contentRef };
}
